import { plainToClass } from "class-transformer";
import { LMSTrainingOverviewDto, LMSTrainingDetailDto, LMSRoleDto, LMSStudyDto } from "./trainings.objects";
import { MultiFileUploadManager } from "./multi-file-upload-manager";
import { IFilter } from "../../../../shared/scripts/filtering/filtering.entities";
import { InitializeFilterDropdown, GetSelectedDropdownValues } from "../../../../shared/scripts/filtering/filtering.utilities";

export class LMSTrainingsPage implements IFilter {
    trainingsTable: any;
    allTrainings: LMSTrainingOverviewDto[];
    trainings: LMSTrainingOverviewDto[];
    materialsUploadManager: MultiFileUploadManager;
    showArchive: boolean = false;
    private editingTrainingId: number | null = null;

    constructor(allTrainingsJson: any) {
        this.allTrainings = plainToClass(LMSTrainingOverviewDto, <LMSTrainingOverviewDto[]>allTrainingsJson);
        this.trainings = this.allTrainings.filter(t => t.isArchived === this.showArchive);

        this.initializeFilters();
        this.initializeSearch();
        this.initializeTable();
        this.initializeCreateEditDialog();
        this.initializeExportDialog();

        $("#toggleTrainingsBtn").on('click', () => this.showActiveTrainings());
        $("#toggleArchivedTrainingsBtn").on('click', () => this.showArchivedTrainings())
        $('#newTrainingBtn').on('click', () => this.openCreateDialog());
        $("#saveTrainingBtn").on('click', () => this.submitTrainingForm());
        $('#exportTrainingsBtn').on('click', () => this.exportTrainings());

        // Guard against a native GET submit wiping the page (see site-study-user-details.page.ts)
        $('#trainingForm').on('submit', (e) => {
            e.preventDefault();
            this.submitTrainingForm();
        });
    }

    private initializeFilters(): void {
        InitializeFilterDropdown("#study-affiliation", [], this);
        InitializeFilterDropdown("#study-dependent", [], this);
        InitializeFilterDropdown('#role', [], this);

        //$('#study-affiliation, #study-dependent, #role').selectpicker('selectAll').selectpicker('refresh');
    }

    private initializeSearch(): void {
        $('#lms-training-search').on('keyup', (e) => {
            this.trainingsTable.search((e.target as HTMLInputElement).value).draw();
        });

        $('#lms-training-search-clear').on('click', () => {
            $('#lms-training-search').val('');
            this.trainingsTable.search('').draw();
        });
    }

    private initializeTable() {
        this.trainingsTable = $('#lmsTrainingTable').DataTable({
            dom: 'rtip',
            autoWidth: false,
            info: false,
            paging: false,
            search: true,
            scrollX: true,
            scrollY: '60vh',
            scrollCollapse: true,
            orderCellsTop: true,
            order: [[0, 'asc']], // order alphabetically by title
            columns: [
                { data: "trainingName", title: "Training Name", className: "text-nowrap" },
                {
                    data: "roles",
                    title: "Roles",
                    render: (data: LMSRoleDto[]) => data.map(r => r.name).join(", ")
                },
                {
                    data: "isStudyIndependent",
                    title: "Study Dependent",
                    render: function (data: boolean) {
                        if (data) {
                            return '<i class="fa-solid fa-xmark text-danger text-center"></i>';
                        }

                        return '<i class="fa-solid fa-check text-success text-center"></i>';
                    }
                },
                {
                    data: "studies",
                    title: "Study",
                    render: (data: LMSStudyDto[]) => data.map(s => s.name).join(", ")
                },
                { data: "timeToComplete", title: "Time to Complete" },
                { data: "expirationTime", title: "Expiration Time" },
                {
                    data: null,
                    title: "",
                    className: "text-center lms-table-col-actions",
                    orderable: false,
                    render: function () {
                        return `<button class="lms-edit-training"><i class="fa-solid fa-pencil"></i></button>`;
                    }
                }
            ], 
            data: this.trainings
        });

        $('#lmsTrainingTable tbody').on('click', '.lms-edit-training', (e) => {
            var row = $(e.currentTarget).closest('tr');
            var data = $("#lmsTrainingTable").DataTable().row(row).data();
            if (!data || !data.trainingId) return;

            this.openEditDialog(data.trainingId);
        });
    }

    private initializeCreateEditDialog() {
        this.materialsUploadManager = new MultiFileUploadManager("#materials-input", "#materials-list");

        document.getElementById("materials-add-btn").addEventListener("click", () => {
            document.getElementById("materials-input").click();
        });

        $('#trainingFormDialog').on('hidden.bs.modal', () => {
            this.materialsUploadManager.reset();
        })

        $('#independent').on('change', (e) => {
            const isIndependent = $(e.currentTarget).is(':checked');
            const studyCheckboxes = $('.lms-study-checkbox');

            studyCheckboxes.prop('disabled', isIndependent);
            if (isIndependent) {
                studyCheckboxes.prop('checked', false);
            }
        })

        $('#can-expire').on('changed.bs.select', () => {
            const canExpire = $("#can-expire").val() === "yes";
            $('#expiration-required').toggle(canExpire);
            $('#expiration-time').prop('disabled', !canExpire);
            if (!canExpire) {
                $('#expiration-time').val('');
            }
        });
    }

    private initializeExportDialog() {
        // reset form on close
        $('#exportTrainingsDialog').on('hidden.bs.modal', () => {
            (document.getElementById('exportTrainingsForm') as HTMLFormElement)?.reset();
        });

        // Guard against a native GET submit wiping the page (see initializeCreateEditDialog)
        $('#exportTrainingsForm').on('submit', (e) => {
            e.preventDefault();
            this.exportTrainings();
        });
    }

    private exportTrainings() {
        const studyIds: number[] = [];
        const roleIds: number[] = [];

        $(".lms-study-export-checkbox:checked").each((_, el) => {
            studyIds.push(Number($(el).data("study-id")));
        });

        $(".lms-role-export-checkbox:checked").each((_, el) => {
            roleIds.push(Number($(el).data("role-id")));
        });

        // Repeated keys are what model-binds to List<int>; a comma-joined value would not
        const params = [
            ...studyIds.map(id => `studyIds=${id}`),
            ...roleIds.map(id => `roleIds=${id}`)
        ].join('&');

        window.location.href = `/secure/lms/settings/trainings?handler=ExportTrainings${params ? `&${params}` : ''}`;
        $('#exportTrainingsDialog').modal('hide');
    }

    public triggerFilter(): void {
        const selectedStudyIds = GetSelectedDropdownValues('#study-affiliation');
        const selectedDependent = GetSelectedDropdownValues('#study-dependent');
        const selectedRoleIds = GetSelectedDropdownValues('#role');

        this.trainings = this.allTrainings.filter(t =>
            t.matchesFilter(selectedStudyIds, selectedDependent, selectedRoleIds) && t.isArchived == this.showArchive
        );
        this.updateTable(this.trainings);
    }

    private showActiveTrainings() {
        // if active trainings are already displayed, do nothing
        if (!this.showArchive) {
            return;
        }

        this.showArchive = false;
        $("#toggleArchivedTrainingsBtn").removeClass('active');
        $("#toggleTrainingsBtn").addClass('active');

        this.triggerFilter();
    }

    private showArchivedTrainings() {
        // if archived trainings are already displayed, do nothing
        if (this.showArchive) {
            return;
        }

        this.showArchive = true;
        $("#toggleTrainingsBtn").removeClass('active');
        $("#toggleArchivedTrainingsBtn").addClass('active');

        this.triggerFilter();
    }

    private updateTable(matchingItems: LMSTrainingOverviewDto[]): void {
        this.trainingsTable.clear();
        this.trainingsTable.rows.add(matchingItems).draw();
    }

    private openCreateDialog() {
        this.editingTrainingId = null;
        $("#trainingDialogTitle").text("New Training").show();
        this.resetForm();
        $('#trainingFormDialog').modal('show');
    }

    private openEditDialog(trainingId: number) {
        this.editingTrainingId = trainingId;
        $("#trainingDialogTitle").text("Edit Training").show();
        $.get(`?handler=TrainingDetails&id=${trainingId}`)
            .done((training: LMSTrainingDetailDto) => {
                this.populateForm(training);
                $('#trainingFormDialog').modal('show');
            })
            //.fail(() => this.showServerError());
    }

    private resetForm() {
        (document.getElementById('trainingForm') as HTMLFormElement)?.reset();
        this.materialsUploadManager.reset();
        $('#trainingFormDialog .is-invalid').removeClass('is-invalid');
        $('.invalid-feedback').text('').hide();
        $('.lms-study-checkbox').prop('disabled', false);
        $('.selectpicker').selectpicker('val', '').selectpicker('refresh');
        $('#expiration-required').hide();
        $('#expiration-time').prop('disabled', false);
        // hide loading icon
        // hide "something went wrong"
    }

    private populateForm(t: any) {
        this.materialsUploadManager.setExistingMaterials(t.materials);

        $('#trainingName').val(t.title);
        $('#instructions').val(t.instructions);
        $('#completion-window').val(t.defaultCompletionWindowInDays);
        $('#completion-time').val(t.expectedCompletionTime);
        $('#can-expire').val(t.canExpire ? 'yes' : 'no').selectpicker('refresh').trigger("changed.bs.select");
        $('#expiration-time').val(t.defaultExpirationTimeInDays != 0 ? t.defaultExpirationTimeInDays : "");
        $('#requires-cert').val(t.requiresUpload ? 'yes' : 'no').selectpicker('refresh');
        $('#notes').val(t.notes);
        $('.lms-role-checkbox').each((_, el) => {
            $(el).prop('checked', t.roleIds.includes(Number($(el).data('role-id'))));
        });
        $('.lms-study-checkbox').each((_, el) => {
            $(el).prop('checked', t.studyIds.includes(Number($(el).data('study-id'))));
        });
        $('#archive-training-checkbox').prop('checked', t.isArchived);

        $('#independent').prop('checked', t.isStudyIndependent).trigger('change');
    }

    private submitTrainingForm() {
        if (!this.validate()) {
            return;
        }

        $("#saveTrainingSpinner").show()

        const isEdit = this.editingTrainingId !== null;
        const payload = this.buildPayload(isEdit ? this.editingTrainingId : 0);
        const handler = isEdit ? 'UpdateTraining' : 'CreateTraining';

        $.ajax({
            url: `?handler=${handler}`,
            method: 'POST',
            contentType: false,
            processData: false,
            headers: { 'RequestVerificationToken': $('input[name="__RequestVerificationToken"]').val() as string },
            data: payload
        }).done((result: LMSTrainingOverviewDto) => {
            $("#saveTrainingSpinner").hide();
            $('#trainingFormDialog').modal('hide');
            this.upsertTrainingRow(plainToClass(LMSTrainingOverviewDto, result));
        }).fail((e) => {
            $("#saveTrainingSpinner").hide();
            // display "something went wrong"
            console.log(e);
        });
    }

    private validate(): boolean {
        $("#trainingFormDialog .is-invalid").removeClass("is-invalid");
        $(".invalid-feedback").text("").hide();

        let isValid = true;

        const nameEl = $("#trainingName");
        if (!(nameEl.val() as string)?.trim()) {
            this.markInvalid(nameEl, "Training name is required.");
            isValid = false;
        }

        const windowEl = $("#completion-window");
        if (!windowEl.val()) {
            this.markInvalid(windowEl, "Completion window is required.");
            isValid = false;
        }

        const timeEl = $("#completion-time");
        if (!timeEl.val()) {
            this.markInvalid(timeEl, "Completion time is required.");
            isValid = false;
        }

        const canExpireEl = $("#can-expire");
        const expirationEl = $("#expiration-time");
        if (!canExpireEl.val()) {
            this.markInvalid(canExpireEl, "Please select one");
            isValid = false;
        } else if (canExpireEl.val() === "yes" && !expirationEl.val()) {
            this.markInvalid(expirationEl, "Expiration time is required when training can expire.");
            isValid = false;
        }

        const certRequiredEl = $("#requires-cert");
        if (!certRequiredEl.val()) {
            this.markInvalid(certRequiredEl, "Please select one");
            isValid = false;
        }

        //const roleChecks = $(".lms-role-checkbox:checked");
        //if (roleChecks.length < 1) {
        //    this.markInvalid($(".lms-role-checkbox").first(), "Select at least one role.");
        //    isValid = false;
        //}

        return isValid;
    }

    private markInvalid(element: JQuery, errorMessage: string) {
        const formGroup = element.closest('.form-group');

        if (element.hasClass("selectpicker")) {
            formGroup.find(".dropdown-toggle").addClass("is-invalid");
        } else if (element.is(":checkbox")) {
            formGroup.find(".lms-form-check").addClass("is-invalid");
        } else {
            element.addClass("is-invalid");
        }

        formGroup.find(".invalid-feedback").text(errorMessage).show();
    }

    private buildPayload(trainingId: number): FormData {
        const formData = new FormData();

        if (trainingId >= 0) {
            formData.append("Id", trainingId.toString());
        }
        formData.append("Title", (($("#trainingName").val() as string) ?? "").trim());
        formData.append("Instructions", ($("#instructions").val() as string) ?? "");
        formData.append("DefaultCompletionWindowInDays", ($("#completion-window").val() as string) ?? "0");
        formData.append("ExpectedCompletionTime", ($("#completion-time").val() as string) ?? "");

        const canExpire = $("#can-expire").val() === "yes";
        formData.append("CanExpire", canExpire.toString());
        formData.append("DefaultExpirationTimeInDays", canExpire ? (($("#expiration-time").val() as string) ?? "0") : "0");

        formData.append("RequiresUpload", ($("#requires-cert").val() === "yes").toString());

        const isIndependent = $("#independent").is(":checked");
        formData.append("IsStudyIndependent", isIndependent.toString());

        formData.append("Notes", ($("#notes").val() as string) ?? "");

        const isArchived = $("#archive-training-checkbox").is(":checked");
        formData.append("IsArchived", isArchived.toString());

        $(".lms-role-checkbox:checked").each((_, el) => {
            formData.append("RoleIds", ($(el).data("role-id") as number).toString());
        });

        if (!isIndependent) {
            $(".lms-study-checkbox:checked").each((_, el) => {
                formData.append("StudyIds", ($(el).data("study-id") as number).toString());
            });
        }

        this.materialsUploadManager.getFiles().forEach(file => {
            formData.append("Materials", file, file.name);
        });

        this.materialsUploadManager.getRemovedMaterialIds().forEach(id => {
            formData.append("RemovedMaterialIds", id.toString());
        });

        return formData;
    }

    private upsertTrainingRow(dto: LMSTrainingOverviewDto) {
        const index = this.allTrainings.findIndex(t => t.trainingId === dto.trainingId);
        if (index >= 0) {
            this.allTrainings[index] = dto;
        } else {
            this.allTrainings.push(dto);
        }

        this.triggerFilter();
    }
}